/**
 * Type-safe, hierarchical query key factory.
 *
 * All React Query keys MUST be defined here — never use raw string arrays.
 * Keys are hierarchical so you can invalidate broadly (e.g., guilds.all)
 * or narrowly (e.g., guilds.detail('gld_1')).
 */
export const queryKeys = {
  // === Guilds ===
  guilds: {
    all: ['guilds'] as const,
    lists: () => [...queryKeys.guilds.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.guilds.lists(), filters] as const,
    details: () => [...queryKeys.guilds.all, 'detail'] as const,
    detail: (guildId: string) => [...queryKeys.guilds.details(), guildId] as const,
    members: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'members'] as const,
    activity: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'activity'] as const,
    invites: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'invites'] as const,
    bans: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'bans'] as const,
    treasury: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'treasury'] as const,
    my: () => [...queryKeys.guilds.all, 'my'] as const,
  },

  // === Game Rewards ===
  gameRewards: {
    all: ['gameRewards'] as const,
    gameConfigs: () => [...queryKeys.gameRewards.all, 'gameConfigs'] as const,
    subscription: () => [...queryKeys.gameRewards.all, 'subscription'] as const,
    subscriptionTiers: () => [...queryKeys.gameRewards.all, 'subscriptionTiers'] as const,
    timePlayed: () => [...queryKeys.gameRewards.all, 'timePlayed'] as const,
    rewardHistory: () => [...queryKeys.gameRewards.all, 'rewardHistory'] as const,
    rewardLog: () => [...queryKeys.gameRewards.all, 'rewardLog'] as const,
    rewardRules: () => [...queryKeys.gameRewards.all, 'rewardRules'] as const,
    rewardTypeLimits: () => [...queryKeys.gameRewards.all, 'rewardTypeLimits'] as const,
    leaderboard: (gameConfigId: string, gameEvent: string) =>
      [...queryKeys.gameRewards.all, 'leaderboard', gameConfigId, gameEvent] as const,
    leaderboardGames: () => [...queryKeys.gameRewards.all, 'leaderboardGames'] as const,
    adminSubscriptions: () => [...queryKeys.gameRewards.all, 'adminSubscriptions'] as const,
  },

  // === Chat ===
  chat: {
    all: ['chat'] as const,
    channels: () => [...queryKeys.chat.all, 'channels'] as const,
    channel: (channelId: string) => [...queryKeys.chat.channels(), channelId] as const,
    channelMembers: (channelId: string) =>
      [...queryKeys.chat.channel(channelId), 'members'] as const,
  },

  // === DFE ===
  dfe: {
    all: ['dfe'] as const,
    definitions: () => [...queryKeys.dfe.all, 'definitions'] as const,
    definitionList: (filters: Record<string, unknown>) =>
      [...queryKeys.dfe.definitions(), filters] as const,
    definition: (itemDefId: string) => [...queryKeys.dfe.definitions(), itemDefId] as const,
    inventory: (filters?: Record<string, unknown>) =>
      [...queryKeys.dfe.all, 'inventory', filters] as const,
  },

  // === DFE Game State ===
  gameState: {
    all: ['gameState'] as const,
    detail: (keyword: string) => [...queryKeys.gameState.all, 'detail', keyword] as const,
    list: () => [...queryKeys.gameState.all, 'list'] as const,
    admin: () => [...queryKeys.gameState.all, 'admin'] as const,
    adminDetail: (keyword: string) => [...queryKeys.gameState.admin(), keyword] as const,
    adminList: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameState.admin(), 'list', filters] as const,
    adminAuditLog: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameState.admin(), 'auditLog', filters] as const,
  },

  // === DFE Inventory Transition ===
  inventoryTransition: {
    all: ['inventoryTransition'] as const,
    importHistory: () => [...queryKeys.inventoryTransition.all, 'importHistory'] as const,
  },

  // === Crafting ===
  crafting: {
    all: ['crafting'] as const,
    recipes: (filters?: Record<string, unknown>) =>
      [...queryKeys.crafting.all, 'recipes', filters] as const,
    recipeDetail: (recipeId: string) => [...queryKeys.crafting.all, 'recipe', recipeId] as const,
    learnedRecipes: (userId?: string) =>
      [...queryKeys.crafting.all, 'learnedRecipes', userId] as const,
    log: (filters?: Record<string, unknown>) =>
      [...queryKeys.crafting.all, 'log', filters] as const,
  },

  // === Favorites ===
  favorites: {
    all: ['favorites'] as const,
    list: () => [...queryKeys.favorites.all, 'list'] as const,
    status: (archId: string) => [...queryKeys.favorites.all, 'status', archId] as const,
  },

  // === Polls ===
  polls: {
    all: ['polls'] as const,
    list: () => [...queryKeys.polls.all, 'list'] as const,
    detail: (pollId: string) => [...queryKeys.polls.all, 'detail', pollId] as const,
    results: (pollId: string) => [...queryKeys.polls.detail(pollId), 'results'] as const,
    myResponse: (pollId: string) => [...queryKeys.polls.detail(pollId), 'myResponse'] as const,
  },

  // === Fiardle ===
  fiardle: {
    all: ['fiardle'] as const,
    stats: () => [...queryKeys.fiardle.all, 'stats'] as const,
    leaderboard: () => [...queryKeys.fiardle.all, 'leaderboard'] as const,
    activeGame: () => [...queryKeys.fiardle.all, 'activeGame'] as const,
  },

  // === Liquidity Caches ===
  liquidityCaches: {
    all: ['liquidityCaches'] as const,
    pools: () => [...queryKeys.liquidityCaches.all, 'pools'] as const,
    pool: (poolId: string) => [...queryKeys.liquidityCaches.pools(), poolId] as const,
    positions: () => [...queryKeys.liquidityCaches.all, 'positions'] as const,
    quote: (poolId: string, params: Record<string, unknown>) =>
      [...queryKeys.liquidityCaches.pool(poolId), 'quote', params] as const,
  },

  // === Mining ===
  mining: {
    all: ['mining'] as const,
    lands: () => [...queryKeys.mining.all, 'lands'] as const,
    log: () => [...queryKeys.mining.all, 'log'] as const,
  },

  // === Plugin Builder ===
  pluginBuilder: {
    all: ['pluginBuilder'] as const,
    sessions: () => [...queryKeys.pluginBuilder.all, 'sessions'] as const,
    publishStatus: (sessionId: string) =>
      [...queryKeys.pluginBuilder.all, 'publishStatus', sessionId] as const,
  },

  // === Learn Anything ===
  learnAnything: {
    all: ['learnAnything'] as const,
    courses: () => [...queryKeys.learnAnything.all, 'courses'] as const,
    curriculum: (courseId: string) =>
      [...queryKeys.learnAnything.courses(), courseId, 'curriculum'] as const,
    progress: (courseId: string) =>
      [...queryKeys.learnAnything.courses(), courseId, 'progress'] as const,
  },

  // === Models ===
  models: {
    all: ['models'] as const,
    list: () => [...queryKeys.models.all, 'list'] as const,
    definition: (modelId: string) => [...queryKeys.models.all, 'definition', modelId] as const,
    definitionByLookup: (provider: string, modelName: string) =>
      [...queryKeys.models.all, 'definition', provider, modelName] as const,
    imageModels: () => [...queryKeys.models.all, 'imageModels'] as const,
  },

  // === Conversations ===
  conversations: {
    all: ['conversations'] as const,
    list: (archeId: string) => [...queryKeys.conversations.all, 'list', archeId] as const,
    detail: (conversationId: string) =>
      [...queryKeys.conversations.all, 'detail', conversationId] as const,
  },

  // === Files & Storage ===
  files: {
    all: ['files'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.files.all, 'list', filters] as const,
    detail: (fileId: string) => [...queryKeys.files.all, 'detail', fileId] as const,
  },

  folders: {
    all: ['folders'] as const,
    list: (parentId?: string | null) => [...queryKeys.folders.all, 'list', parentId] as const,
    detail: (folderId: string) => [...queryKeys.folders.all, 'detail', folderId] as const,
    breadcrumb: (folderId: string) => [...queryKeys.folders.all, 'breadcrumb', folderId] as const,
  },

  storage: {
    all: ['storage'] as const,
    usage: () => [...queryKeys.storage.all, 'usage'] as const,
    operations: () => [...queryKeys.storage.all, 'operations'] as const,
  },
} as const;
